import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import Link from 'components/elements/Link';
import Markdown from 'components/elements/Markdown';
import { Container } from 'components/elements/Grid';

import { Section, Content, BigText, Title, SubtitleMarkdown, ImgWrap } from './styles';

function Info({ data }) {
  const { image } = useStaticQuery(
    graphql`
      {
        image: file(sourceInstanceName: { eq: "images" }, relativePath: { eq: "404_image.png" }) {
          childImageSharp {
            gatsbyImageData(width: 378, layout: CONSTRAINED, formats: [AVIF, WEBP, AUTO])
          }
        }
      }
    `,
  );
  const bigText = data?.bigText || '404';
  const isGlobalError = data?.isGlobalError;
  const subtitle = data?.subtitle?.markdown?.html;
  const customSubtitle = !subtitle && isGlobalError ? '<p>Oops! Something went wrong…</p>' : subtitle;
  const illustration = data?.illustration?.gatsbyImageData;

  return (
    <Section>
      <Container>
        <Content isGlobalError={isGlobalError}>
          <div>
            {isGlobalError ? <BigText>{bigText}</BigText> : <Title>{data.title}</Title>}
            {customSubtitle && (
              <SubtitleMarkdown isGlobalError={isGlobalError}>
                <Markdown>{customSubtitle}</Markdown>
              </SubtitleMarkdown>
            )}
            {(data?.link?.url || isGlobalError) && (
              <Link
                title={data?.link?.title || 'back to homepage'}
                url={data?.link?.url || '/'}
                isButton
                hasBackground
              />
            )}
          </div>
          {(illustration || isGlobalError) && (
            <ImgWrap isGlobalError={isGlobalError}>
              <GatsbyImage image={illustration || image?.childImageSharp?.gatsbyImageData} alt="" />
            </ImgWrap>
          )}
        </Content>
      </Container>
    </Section>
  );
}

export default Info;

PropTypes.shape({
  bigText: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.shape({
    markdown: PropTypes.shape({
      html: PropTypes.string.isRequired,
    }),
  }),
  illustration: PropTypes.shape({
    gatsbyImageData: PropTypes.shape({
      height: PropTypes.number,
      images: PropTypes.shape({
        fallback: PropTypes.shape({
          sizes: PropTypes.string,
          src: PropTypes.string,
          srcSet: PropTypes.string,
        }),
        sources: PropTypes.arrayOf(
          PropTypes.shape({
            sizes: PropTypes.string,
            srcSet: PropTypes.string,
            type: PropTypes.string,
          }),
        ),
      }),
      layout: PropTypes.string,
      width: PropTypes.number,
    }),
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  link: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string.isRequired,
    isButton: PropTypes.bool.isRequired,
    hasBackground: PropTypes.bool.isRequired,
  }),
  metaKeywords: PropTypes.string,
  isGlobalError: PropTypes.bool.isRequired,
});
